import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, TextField, Typography, CircularProgress, Chip, IconButton, Tooltip } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Progress } from '../../../../components/tailwind-component/ui/progress';
import useWizardStepHelp from '../guide/useWizardStepHelp';
import { STOREFRONT_GUIDE } from '../guide/guideContent';
import { StorefrontImageUpload } from '../../../../components/storefront-image-upload/storefront-image-upload';
import { InfluencerStorefrontService } from '../../../../core/services/influencer-storefront.service';
import { LocalService } from '../../../../core/services';
import { BASE_URL_UI } from '../../../../config/config';
import {
  CURRENCIES,
  STOREFRONT_STEPS,
  StorefrontDraft,
  canFinish,
  emptyDraft,
  regeneratedSlug,
  reviewRows,
  slugify,
  stepError,
  suggestDescription,
} from './storefrontSetup';

/**
 * First-run storefront setup, one question at a time.
 *
 * Replaces the single long form a creator used to meet on their very first
 * visit — see the note at the top of storefrontSetup.ts for why. The old form
 * is still what Store Settings renders, which is the right place to change
 * everything at once.
 *
 * Mobile counterpart: StorefrontSetupWizard.tsx in clanconnect_mobile.
 */

const localService = new LocalService();
const BASE_HOST = (BASE_URL_UI || '').replace(/^https?:\/\//, '');

interface Props {
  influencerId: number;
  accountId: number;
  userId: number;
  displayName?: string;
  isUS?: boolean;
  onCreated: (row: any) => void;
}

export const StorefrontWizard: React.FC<Props> = ({
  influencerId,
  accountId,
  userId,
  displayName,
  isUS,
  onCreated,
}) => {
  const [stepIndex, setStepIndex] = useState(0);
  // ⓘ next to "Step X of 4" — the same step help as the Zap setup.
  const { helpButton, helpOverlay } = useWizardStepHelp(STOREFRONT_GUIDE, stepIndex, 'storefront_step_help_seen');
  const [draft, setDraft] = useState<StorefrontDraft>(() => emptyDraft({ displayName, isUS }));
  const [slugChecking, setSlugChecking] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const [showError, setShowError] = useState(false);
  const [saving, setSaving] = useState(false);
  // Synchronous, unlike state — a second click lands before a re-render and
  // would otherwise create the shop twice, as the Zap form flow once did.
  const savingRef = useRef(false);
  const topRef = useRef<HTMLDivElement | null>(null);

  const step = STOREFRONT_STEPS[stepIndex];
  const isLast = stepIndex === STOREFRONT_STEPS.length - 1;
  const error = stepError(step.id, draft, slugAvailable);
  const uploadPath = `accounts/${accountId}/influencer/${influencerId}/storefront`;

  const set = (patch: Partial<StorefrontDraft>) => setDraft((d) => ({ ...d, ...patch }));

  // The description is written for them when they arrive at the About step, so
  // the box is never an empty stare. Only once, and never over their own words.
  const descriptionSeeded = useRef(false);
  useEffect(() => {
    if (step.id !== 'about' || descriptionSeeded.current || draft.store_description) return;
    descriptionSeeded.current = true;
    set({ store_description: suggestDescription(draft.store_name, displayName) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.id]);

  // Next lands at the top of the new question, not wherever the last one was
  // scrolled to.
  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [stepIndex]);

  // Availability, debounced. Nothing to check below 3 characters.
  useEffect(() => {
    const s = draft.store_slug;
    if (!s || s.length < 3) {
      setSlugAvailable(null);
      return;
    }
    setSlugChecking(true);
    const t = setTimeout(() => {
      InfluencerStorefrontService.checkSlug(s, influencerId, accountId)
        .then((res: any) => setSlugAvailable(res?.data?.available ?? null))
        .catch(() => setSlugAvailable(null))
        .finally(() => setSlugChecking(false));
    }, 400);
    return () => {
      clearTimeout(t);
      setSlugChecking(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.store_slug, influencerId, accountId]);

  // Typing a name shapes the link too, until they edit the link themselves.
  const [slugTouched, setSlugTouched] = useState(false);
  const handleName = (value: string) => {
    setShowError(false);
    setDraft((d) => ({
      ...d,
      store_name: value,
      store_slug: slugTouched ? d.store_slug : slugify(value),
    }));
  };

  const handleNext = () => {
    if (error) {
      setShowError(true);
      return;
    }
    setShowError(false);
    setStepIndex((i) => Math.min(i + 1, STOREFRONT_STEPS.length - 1));
  };

  const handleBack = () => {
    setShowError(false);
    setStepIndex((i) => Math.max(i - 1, 0));
  };

  const handleCreate = async () => {
    if (savingRef.current) return;
    if (!canFinish(draft, slugAvailable)) {
      setShowError(true);
      setStepIndex(0);
      return;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      const res: any = await InfluencerStorefrontService.setupStorefront({
        ...draft,
        influencer_id: influencerId,
        account_id: accountId,
        user_id: userId,
      });
      if (res?.status === 1 && res?.data?.rows?.length) {
        localService.toastify('Your storefront is now live!', 'success', 3000);
        onCreated(res.data.rows[0]);
      } else {
        localService.toastify(res?.info || 'Failed to create your storefront', 'error', 3000);
        savingRef.current = false;
      }
    } catch {
      localService.toastify('Failed to create your storefront', 'error', 3000);
      savingRef.current = false;
    } finally {
      setSaving(false);
    }
  };

  const liveUrl = draft.store_slug ? `${BASE_HOST}/shop/${draft.store_slug}` : `${BASE_HOST}/shop/…`;

  const slugStatus = useMemo(() => {
    if (!draft.store_slug || draft.store_slug.length < 3) return null;
    if (slugChecking) return { ok: null as boolean | null, text: 'Checking…' };
    if (slugAvailable === true) return { ok: true, text: 'This link is yours' };
    if (slugAvailable === false) return { ok: false, text: 'Taken — try another' };
    return null;
  }, [draft.store_slug, slugChecking, slugAvailable]);

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto' }} ref={topRef}>
      {helpOverlay}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#6b7280' }}>
              Step {stepIndex + 1} of {STOREFRONT_STEPS.length}
            </Typography>
            {helpButton}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {STOREFRONT_STEPS.map((s, i) => (
              <Typography
                key={s.id}
                sx={{
                  fontSize: 12,
                  fontWeight: i === stepIndex ? 700 : 500,
                  color: i === stepIndex ? '#7c3aed' : i < stepIndex ? '#111' : '#c4c4cc',
                }}
              >
                {s.stepLabel}
                {i < STOREFRONT_STEPS.length - 1 ? '  ›' : ''}
              </Typography>
            ))}
          </Box>
        </Box>
        <Progress value={((stepIndex + 1) / STOREFRONT_STEPS.length) * 100} />
      </Box>

      <Box sx={{ border: '1px solid #ececf0', borderRadius: '14px', bgcolor: '#fff', p: { xs: 2, md: 3 } }}>
        <Typography sx={{ fontSize: { xs: 18, md: 20 }, fontWeight: 700, color: '#111' }}>
          {step.title}
        </Typography>
        <Typography sx={{ fontSize: 14, color: '#6b7280', mt: 0.5, mb: 2.5 }}>{step.subtitle}</Typography>

        {step.id === 'name' && (
          <Box>
            <TextField
              fullWidth
              autoFocus
              label="Shop name"
              value={draft.store_name}
              onChange={(e) => handleName(e.target.value)}
              error={showError && !!error && !draft.store_name.trim()}
              inputProps={{ maxLength: 60 }}
            />

            <Box sx={{ mt: 2.5 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#374151', mb: 0.75 }}>
                Your shop link
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: 14, color: '#9ca3af', whiteSpace: 'nowrap' }}>
                  {BASE_HOST}/shop/
                </Typography>
                <TextField
                  size="small"
                  sx={{ flex: 1 }}
                  value={draft.store_slug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    setShowError(false);
                    set({ store_slug: slugify(e.target.value) });
                  }}
                  error={showError && slugAvailable === false}
                />
                <Tooltip title="Suggest another link">
                  <IconButton
                    onClick={() => {
                      setSlugTouched(true);
                      set({ store_slug: regeneratedSlug(displayName || draft.store_name) });
                    }}
                  >
                    <AutorenewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              {!!slugStatus && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
                  {slugStatus.ok === true && <CheckCircleIcon sx={{ fontSize: 14, color: '#16a34a' }} />}
                  {slugStatus.ok === false && <ErrorOutlineIcon sx={{ fontSize: 14, color: '#dc2626' }} />}
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: slugStatus.ok === true ? '#16a34a' : slugStatus.ok === false ? '#dc2626' : '#6b7280',
                    }}
                  >
                    {slugStatus.text}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {step.id === 'look' && (
          <Box sx={{ display: 'grid', gap: 2.5 }}>
            <StorefrontImageUpload
              label="Logo"
              hint="Square works best — around 500 × 500"
              value={draft.store_logo}
              uploadPath={uploadPath}
              previewVariant="logo"
              onChange={(m) =>
                set({
                  store_logo: m.url,
                  logo_file_name: m.file_name,
                  logo_file_type: m.file_type,
                  logo_file_path: m.file_path,
                  logo_storage_size: m.storage_size,
                })
              }
              onRemove={() =>
                set({ store_logo: '', logo_file_name: '', logo_file_type: '', logo_file_path: '', logo_storage_size: '' })
              }
            />
            <StorefrontImageUpload
              label="Cover image"
              hint="Wide banner across the top — around 1600 × 500"
              value={draft.store_banner}
              uploadPath={uploadPath}
              previewVariant="banner"
              onChange={(m) =>
                set({
                  store_banner: m.url,
                  banner_file_name: m.file_name,
                  banner_file_type: m.file_type,
                  banner_file_path: m.file_path,
                  banner_storage_size: m.storage_size,
                })
              }
              onRemove={() =>
                set({ store_banner: '', banner_file_name: '', banner_file_type: '', banner_file_path: '', banner_storage_size: '' })
              }
            />
          </Box>
        )}

        {step.id === 'about' && (
          <Box>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Description"
              value={draft.store_description}
              onChange={(e) => set({ store_description: e.target.value })}
              inputProps={{ maxLength: 300 }}
              helperText={`${draft.store_description.length}/300`}
            />
            <TextField
              fullWidth
              sx={{ mt: 2 }}
              label="Website or social link (optional)"
              placeholder="https://instagram.com/yourhandle"
              value={draft.website_url}
              onChange={(e) => {
                setShowError(false);
                set({ website_url: e.target.value });
              }}
              error={showError && !!error}
              helperText={showError && error ? error : ' '}
            />
          </Box>
        )}

        {step.id === 'review' && (
          <Box>
            <StorefrontPreview draft={draft} />

            <Box sx={{ mt: 2.5 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#374151', mb: 1 }}>
                Prices shown in
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {CURRENCIES.filter((c) => c !== 'INR' || !isUS || draft.default_currency === 'INR').map((c) => (
                  <Chip
                    key={c}
                    label={c}
                    onClick={() => set({ default_currency: c })}
                    sx={{
                      fontWeight: 600,
                      bgcolor: draft.default_currency === c ? '#7c3aed' : '#f5f3ff',
                      color: draft.default_currency === c ? '#fff' : '#7c3aed',
                      '&:hover': { bgcolor: draft.default_currency === c ? '#6d28d9' : '#ede9fe' },
                    }}
                  />
                ))}
              </Box>
            </Box>

            <Box sx={{ mt: 2.5, borderTop: '1px solid #f3f4f6', pt: 2 }}>
              {reviewRows(draft).map((row) => (
                <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography sx={{ fontSize: 13, color: '#6b7280' }}>{row.label}</Typography>
                  <Typography sx={{ fontSize: 13, color: row.missing ? '#9ca3af' : '#111', fontWeight: row.missing ? 400 : 600 }}>
                    {row.value}
                  </Typography>
                </Box>
              ))}
              <Typography sx={{ fontSize: 12, color: '#9ca3af', mt: 1 }}>
                Anything left for later lives in Store Settings, once your shop is open.
              </Typography>
            </Box>
          </Box>
        )}

        {showError && !!error && step.id === 'name' && (
          <Typography sx={{ fontSize: 13, color: '#dc2626', mt: 1.5 }}>{error}</Typography>
        )}
      </Box>

      {/* A shop with no images still looks like a shop — show it from step 2 on. */}
      {step.id !== 'review' && stepIndex > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#7c3aed', mb: 1 }}>LIVE PREVIEW</Typography>
          <StorefrontPreview draft={draft} />
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, pt: 2, borderTop: '1px solid #ececf0' }}>
        <Button variant="outlined" onClick={handleBack} disabled={stepIndex === 0} startIcon={<ChevronLeftIcon />}>
          Back
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography sx={{ fontSize: 12, color: '#9ca3af', display: { xs: 'none', md: 'block' } }} noWrap>
            {liveUrl}
          </Typography>
          {isLast ? (
            <Button variant="contained" size="large" onClick={handleCreate} disabled={saving}>
              {saving ? <CircularProgress size={20} color="inherit" /> : 'Open my shop'}
            </Button>
          ) : (
            <Button variant="contained" size="large" onClick={handleNext} endIcon={<ChevronRightIcon />}>
              {step.optional && !stepIsFilled(step.id, draft) ? 'Skip' : 'Next'}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

/** Whether an optional step has anything in it — decides Skip vs Next. */
function stepIsFilled(id: string, draft: StorefrontDraft): boolean {
  if (id === 'look') return !!(draft.store_logo || draft.store_banner);
  if (id === 'about') return !!(draft.store_description || draft.website_url);
  return true;
}

/** The shop as shoppers will see it, at card size. */
const StorefrontPreview: React.FC<{ draft: StorefrontDraft }> = ({ draft }) => (
  <Box sx={{ border: '1px solid #ececf0', borderRadius: '14px', overflow: 'hidden', bgcolor: '#fff' }}>
    <Box
      sx={{
        height: 96,
        // No cover yet? A brand gradient, never an empty broken image.
        background: draft.store_banner
          ? undefined
          : 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 55%, #c4b5fd 100%)',
      }}
    >
      {!!draft.store_banner && (
        <img src={draft.store_banner} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      )}
    </Box>
    <Box sx={{ px: 2, pb: 2, textAlign: 'center' }}>
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: '12px',
          border: '4px solid #fff',
          mx: 'auto',
          mt: '-32px',
          bgcolor: '#f5f3ff',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 6px 18px rgba(15,23,42,.12)',
        }}
      >
        {draft.store_logo ? (
          <img src={draft.store_logo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#7c3aed' }}>
            {(draft.store_name || '?').trim().charAt(0).toUpperCase()}
          </Typography>
        )}
      </Box>
      <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#111', mt: 1 }}>
        {draft.store_name || 'Your shop'}
      </Typography>
      {!!draft.store_description && (
        <Typography sx={{ fontSize: 13, color: '#6b7280', mt: 0.5 }}>{draft.store_description}</Typography>
      )}
      <Typography sx={{ fontSize: 12, color: '#9ca3af', mt: 0.75 }} noWrap>
        {BASE_HOST}/shop/{draft.store_slug || '…'}
      </Typography>
    </Box>
  </Box>
);

export default StorefrontWizard;
